import { useState, useEffect } from 'react';
import { generateContentWithFallback } from '@/lib/geminiClient';
import { useSession } from '@/lib/SessionContext';
import { useAsaPersonality } from '@/hooks/useAsaPersonality';

export function useAsaMessageGenerator(triggerObject, fallbackTextBuilder, picName = null) {
  const [aiMessage, setAiMessage] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const { member } = useSession();
  const { sapaan, isFadli } = useAsaPersonality();

  useEffect(() => {
    if (!triggerObject) {
      setAiMessage('');
      setIsAiLoading(false);
      return;
    }

    const fallbackText = fallbackTextBuilder ? fallbackTextBuilder() : '';

    const fetchMessage = async () => {
      setIsAiLoading(true);
      try {
        if (!member) throw new Error("No member context");

        // --- SISTEM CACHING ---
        // Buat ID unik berdasarkan apa yang mau diomongin dan siapa usernya
        const cacheKey = `asa_cache_${member.id}_${btoa(encodeURIComponent(fallbackText))}`;
        const cachedResponse = sessionStorage.getItem(cacheKey);
        
        if (cachedResponse) {
          setAiMessage(cachedResponse);
          setIsAiLoading(false);
          return; // Langsung return, gak perlu nembak API lagi!
        }
        // ----------------------

        let routingContext = "";
        if (picName) {
          const loginName = member.full_name || "User";
          const isPic = loginName.toLowerCase() === picName.toLowerCase() || (picName === 'Tim Purchasing' && member.role === 'purchasing');
          
          if (isPic) {
            routingContext = `INFORMASI PENTING: Data ini adalah tanggung jawabku langsung (${picName}). Tegur atau peringatkan aku secara langsung!`;
          } else {
            routingContext = `INFORMASI PENTING: Data ini BUKAN tanggung jawabku, melainkan tanggung jawab "${picName}". Tugasku hanya melihat. Sampaikan pesan ini kepadaku sebagai informasi atau suruh aku untuk menegur/mengingatkan si ${picName}!`;
          }
        }

        const prompt = `Kamu adalah ASA, asisten AI cerdas di Satala Dermatech. Aku (user yang login) bernama ${sapaan}.
        Info sifatku/kepribadianku: ${member.asa_personality || (isFadli ? 'Aku Fadli si bestie developer' : 'Santai dan asik.')}
        ${routingContext}
        
        Tugasmu: Sampaikan pesan atau pertanyaan operasional ini kepadaku: "${fallbackText}"
        Gunakan gaya bahasa yang SANGAT SESUAI dengan kepribadianku di atas. 
        Tulis ulang pesan di atas tapi buat senatural mungkin sesuai gayaku.
        Sampaikan informasinya sejelas mungkin.
        Maksimal 2-3 kalimat saja. Tanpa tanda kutip di awal/akhir.`;
        
        const response = await generateContentWithFallback("Kamu adalah asisten ASA.", prompt, []);
        const finalMessage = response.trim();
        
        // Simpan hasil ke cache biar nanti kalau muncul lagi langsung instan
        sessionStorage.setItem(cacheKey, finalMessage);
        
        setAiMessage(finalMessage);
      } catch (error) {
        console.error("Gagal generate pesan ASA AI:", error);
        setAiMessage(fallbackText);
      } finally {
        setIsAiLoading(false);
      }
    };

    fetchMessage();
  }, [triggerObject, member, sapaan, isFadli, picName]);

  return { aiMessage, isAiLoading };
}
